import * as React from 'react';
import {
  Box,
  Button,
  Icon,
  ParagraphText,
  StackLayout,
} from '@leagueplatform/genesis-core';
import { AngleNextIcon, IconButton } from '@web-config-app/core-react-ui';
import { useIntl } from '@leagueplatform/locales';
import type { EntityDataSourceType } from '@web-config-app/core';
import { ConditionalActionConditionItem } from './conditional-action-condition-item.component';
import { GoToPicker } from './go-to-picker.component';
import { focusById } from './focus-by-id';
import { Pill } from './pill.component';
import { goToAction, getGoToLinkId } from './types';
import type {
  ConditionalActionCondition,
  ConditionalActionConditionSubject,
  ConditionalActionRule,
  ValueCatalogMapping,
} from './types';

interface ConditionalActionRuleCardProps {
  rule: ConditionalActionRule;
  ruleIndex: number;
  rulePath: string;
  conditionSubjects: ConditionalActionConditionSubject[];
  /**
   * Data of the array item the conditional-action control is editing (the
   * enclosing question). Passed to each condition item to resolve its
   * subject's value type and comparison-value options off relative paths.
   */
  enclosingItemData: unknown;
  /**
   * The configured action: `goTo` renders the THEN destination picker;
   * `show` (visibility) renders no THEN row — the rule's effect is fixed
   * (a matched rule shows the question), so there's nothing to pick.
   */
  actionType: 'goTo' | 'show';
  /** Locale key for the action verb pill (schema-declared, with generic fallback). */
  actionLabelKey: string;
  /** Optional allow-list of operator IDs offered by this schema instance. */
  allowedOperators: string[] | undefined;
  /** Optional per-instance operator label-key overrides. */
  operatorLabelOverrides: Record<string, string> | undefined;
  /** Data source name for the destination picker (read from the schema). */
  destinationDataSource: string | undefined;
  /** Consumer type for the destination data source (read from the schema). */
  destinationDataSourceType: EntityDataSourceType;
  /** Mapping for the destination picker (read from the schema). */
  destinationMapping: ValueCatalogMapping | undefined;
  /**
   * Whether the rule may hold more than one condition (schema-declared).
   * `false` hides the "add condition" affordance; `undefined`/`true` shows it.
   * v1 sets this `false`: with only the `currentArrayItem` subject, ANDing a second
   * condition on the same answer is redundant/contradictory — multi-condition
   * rules only become useful once cross-question / data-point subjects exist.
   */
  allowMultipleConditions: boolean | undefined;
  enabled: boolean;
  onChange: (next: ConditionalActionRule) => void;
  onDelete: () => void;
}

export const ConditionalActionRuleCard: React.FC<
  ConditionalActionRuleCardProps
> = ({
  rule,
  ruleIndex,
  rulePath,
  conditionSubjects,
  enclosingItemData,
  actionType,
  actionLabelKey,
  allowedOperators,
  operatorLabelOverrides,
  destinationDataSource,
  destinationDataSourceType,
  destinationMapping,
  allowMultipleConditions,
  enabled,
  onChange,
  onDelete,
}) => {
  const { formatMessage } = useIntl();
  const conditions = rule.conditions ?? [];
  const conditionsEditPath = `${rulePath}.conditions`;
  // Hide "add condition" when the schema restricts rules to one condition.
  // Default (undefined) allows multiple; only an explicit `false` hides it.
  const allowAddCondition = allowMultipleConditions !== false;
  const actionValue = getGoToLinkId(rule.then);
  const addConditionButtonId = `${rulePath}.addCondition`;

  const updateCondition = (index: number, next: ConditionalActionCondition) => {
    const nextConditions = conditions.map((condition, i) =>
      i === index ? next : condition,
    );
    onChange({ ...rule, conditions: nextConditions });
  };

  const removeCondition = (index: number) => {
    const nextConditions = conditions.filter((_, i) => i !== index);
    onChange({ ...rule, conditions: nextConditions });
    // The remove button just unmounted with its row — keep keyboard
    // focus in the rule: previous condition's operator select, or the
    // Add-condition button when the first condition was removed.
    focusById(
      index > 0
        ? `${conditionsEditPath}.${index - 1}.operatorId`
        : addConditionButtonId,
    );
  };

  const addCondition = () => {
    // Seed with the first declared subject. Operator and inputs stay
    // empty — author picks the operator from the dropdown, and
    // selecting the operator seeds its own `defaultInputs()`. With no
    // subjects declared the schema is misconfigured; silently no-op
    // (matches `addRule`'s policy in the parent).
    const defaultSubject = conditionSubjects[0];
    if (!defaultSubject) return;
    const next: ConditionalActionCondition = {
      subjectType: defaultSubject.type,
      operatorId: undefined,
      inputs: {},
    };
    onChange({ ...rule, conditions: [...conditions, next] });
  };

  const hasSubjects = conditionSubjects.length > 0;

  return (
    <StackLayout
      orientation="vertical"
      spacing="$one"
      // Scope the rule's fields for assistive tech — screen readers
      // announce "Rule N, group" when entering, so the sentence tokens
      // ("If … then go to …") have a programmatic container.
      role="group"
      aria-label={formatMessage(
        { id: 'CONDITIONAL_ACTION_RULE_NUMBER_LABEL' },
        { number: ruleIndex + 1 },
      )}
      css={{
        width: '100%',
        border: 'solid $colors$onSurfaceBorderSubdued $borderWidths$thin',
        borderRadius: '$medium',
        padding: '$half $one',
        backgroundColor: '$surfaceBackgroundPrimary',
      }}
    >
      <StackLayout
        orientation="horizontal"
        horizontalAlignment="spaceBetween"
        verticalAlignment="center"
        css={{ width: '100%' }}
      >
        <Box>
          <ParagraphText css={{ typography: '$overline' }}>
            {formatMessage(
              { id: 'CONDITIONAL_ACTION_RULE_NUMBER_LABEL' },
              { number: ruleIndex + 1 },
            )}
          </ParagraphText>
        </Box>
        {enabled && (
          <IconButton
            tooltip={formatMessage({
              id: 'CONDITIONAL_ACTION_DELETE_RULE_ARIA',
            })}
            label={formatMessage({ id: 'CONDITIONAL_ACTION_DELETE_RULE_ARIA' })}
            onClick={onDelete}
            icon="interfaceDelete"
            size="small"
            priority="secondary"
            quiet
            destructive
          />
        )}
      </StackLayout>
      <Box
        css={{
          display: 'flex',
          flexDirection: 'column',
          gap: '$half',
        }}
      >
        <Box
          css={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            gap: '$half',
          }}
        >
          {conditions[0] ? (
            <ConditionalActionConditionItem
              key={0}
              condition={conditions[0]}
              index={0}
              conditionSubjects={conditionSubjects}
              enclosingItemData={enclosingItemData}
              allowedOperators={allowedOperators}
              operatorLabelOverrides={operatorLabelOverrides}
              conditionsEditPath={conditionsEditPath}
              enabled={enabled}
              onUpdate={(next) => updateCondition(0, next)}
              onRemove={() => removeCondition(0)}
              canRemove={conditions.length > 1}
            />
          ) : null}
        </Box>
        {conditions.slice(1).map((condition, i) => {
          const index = i + 1;
          return (
            <Box
              key={index}
              css={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: '$half',
              }}
            >
              <ConditionalActionConditionItem
                condition={condition}
                index={index}
                conditionSubjects={conditionSubjects}
                enclosingItemData={enclosingItemData}
                allowedOperators={allowedOperators}
                operatorLabelOverrides={operatorLabelOverrides}
                conditionsEditPath={conditionsEditPath}
                enabled={enabled}
                onUpdate={(next) => updateCondition(index, next)}
                onRemove={() => removeCondition(index)}
              />
            </Box>
          );
        })}
      </Box>
      {enabled && hasSubjects && allowAddCondition && (
        <Box
          css={{
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%',
          }}
        >
          <Button
            id={addConditionButtonId}
            priority="tertiary"
            quiet
            size="small"
            onClick={addCondition}
          >
            <Icon
              icon="interfacePlus"
              size="$threeQuarters"
              css={{ marginInlineEnd: '$quarter' }}
            />
            {formatMessage({ id: 'CONDITIONAL_ACTION_ADD_CONDITION_LABEL' })}
          </Button>
        </Box>
      )}
      {/* THEN row — the rule's effect, stated explicitly. `goTo` pairs the
          verb pill with a destination picker; `show` (visibility) has a fixed
          effect ("show this question") with nothing to pick, so it renders
          the verb as a static pill and no picker. */}
      <Box
        css={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '$half',
        }}
      >
        <Box
          css={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '$half',
            minWidth: '4rem',
          }}
        >
          <Box
            css={{
              display: 'inline-flex',
              color: '$onSurfaceTextSubdued',
            }}
          >
            <AngleNextIcon />
          </Box>
          <ParagraphText emphasis="subtle">
            {formatMessage({ id: 'CONDITIONAL_ACTION_THEN_LABEL' })}
          </ParagraphText>
        </Box>
        {actionType === 'goTo' ? (
          <>
            {/* The verb pill IS the destination select's visible label — real
                <label for>, no aria duplication. */}
            <Pill htmlFor={`${rulePath}.then`}>
              {formatMessage({ id: actionLabelKey })}
            </Pill>
            <GoToPicker
              id={`${rulePath}.then`}
              value={actionValue}
              onChange={(next) =>
                onChange({ ...rule, then: next ? goToAction(next) : undefined })
              }
              placeholderKey="CONDITIONAL_ACTION_CHOOSE_DESTINATION"
              enabled={enabled}
              dataSourceName={destinationDataSource}
              dataSourceType={destinationDataSourceType}
              mapping={destinationMapping}
            />
          </>
        ) : (
          // `show`: a fixed effect, no field to label — a plain (non-label) pill.
          <Pill>{formatMessage({ id: actionLabelKey })}</Pill>
        )}
      </Box>
    </StackLayout>
  );
};
